'use client';

import { useEffect } from 'react';
import { capturePostHogException } from '@/lib/posthog-client-errors';

interface GlobalErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

/**
 * Global Error Boundary (Root Level)
 *
 * This catches errors that occur in the root layout itself.
 * It must define its own <html> and <body> tags since the root layout may have failed.
 */
export default function GlobalError({ error, reset }: GlobalErrorProps) {
  useEffect(() => {
    capturePostHogException(error, { boundary: 'app/global-error' });
    console.error('[RootError]', error);
  }, [error]);

  const isNetworkError =
    error.message.includes('fetch') ||
    error.message.includes('network') ||
    error.message.includes('ECONNREFUSED');

  return (
    <html lang="en" className="notranslate" translate="no">
      <head>
        <meta name="google" content="notranslate" />
      </head>
      <body
        className="notranslate"
        translate="no"
        style={{
          margin: 0,
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          backgroundColor: '#fafafa',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem',
        }}
      >
        <div style={{ maxWidth: '28rem', textAlign: 'center' }}>
          <div
            style={{
              width: '4rem',
              height: '4rem',
              margin: '0 auto 1.5rem',
              backgroundColor: '#fef3c7',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#d97706"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
              <path d="M12 9v4" />
              <path d="M12 17h.01" />
            </svg>
          </div>

          <h1
            style={{
              fontSize: '1.5rem',
              fontWeight: 'bold',
              color: '#18181b',
              margin: '0 0 0.5rem',
            }}
          >
            {isNetworkError ? 'Service Unavailable' : 'Application Error'}
          </h1>

          <p style={{ color: '#71717a', margin: '0 0 1.5rem', lineHeight: 1.5 }}>
            {isNetworkError
              ? 'The platform is currently unable to connect to required services. This may be temporary maintenance.'
              : 'Something went wrong loading the application. Please try again.'}
          </p>

          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
            <button
              onClick={reset}
              style={{
                padding: '0.625rem 1.25rem',
                backgroundColor: '#18181b',
                color: 'white',
                border: 'none',
                borderRadius: '0.375rem',
                fontWeight: 500,
                cursor: 'pointer',
                fontSize: '0.875rem',
              }}
            >
              Try Again
            </button>
            <button
              onClick={() => (window.location.href = '/login')}
              style={{
                padding: '0.625rem 1.25rem',
                backgroundColor: 'white',
                color: '#18181b',
                border: '1px solid #e4e4e7',
                borderRadius: '0.375rem',
                fontWeight: 500,
                cursor: 'pointer',
                fontSize: '0.875rem',
              }}
            >
              Go to Login
            </button>
          </div>

          {process.env.NODE_ENV === 'development' && (
            <details
              style={{
                marginTop: '1.5rem',
                padding: '1rem',
                backgroundColor: '#f4f4f5',
                borderRadius: '0.5rem',
                textAlign: 'left',
                border: '1px solid #e4e4e7',
              }}
            >
              <summary
                style={{
                  cursor: 'pointer',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  color: '#3f3f46',
                }}
              >
                Error Details
              </summary>
              <pre
                style={{
                  marginTop: '0.5rem',
                  fontSize: '0.75rem',
                  color: '#52525b',
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {error.message}
                {error.stack && `\n\n${error.stack}`}
              </pre>
            </details>
          )}
        </div>
      </body>
    </html>
  );
}
